import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  IconButton
} from '@mui/material';
import { 
  Send as SendIcon, 
  Info as InfoIcon, 
  Close as CloseIcon 
} from '@mui/icons-material';

import DoneIcon from '@mui/icons-material/Done';
import axios from 'axios';
import { apiConfigNhia, nhiaGenCCC } from '../BaseURL';


const Ccc = ({ open, onClose ,memberNumber ,onSubmit }) => {
 
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [cardType, setCardType] = useState();
  const [cardNo, setCardNo] = useState(memberNumber);
  const [apiConfig, setApiConfig] = useState(null);

  

  // Fetch API config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(apiConfigNhia);
        if (response.data.success) {
          setApiConfig(response.data.config);
        } else {
          setError('Failed to load API configuration');
        }
      } catch (err) {
        console.error('Failed to fetch API config:', err);
        setError('Failed to load API configuration');
      }
    };
    fetchConfig();
  }, []);

  // API Configuration (now dynamic)
  // const API_CONFIG = {
  //   baseUrl: 'https://elig.nhia.gov.gh:5000',
  //   endpoint: '/api/hmis/genCCC',
  //   key: 'HPAPI10801',
  //   secret: '6VFl7Tb8?EhtwgQWKZmuCNYxD'
  // };

  /*  ── WHAT A 401 ACTUALLY MEANS, AND WHO CAN FIX IT ────────────────────────
   *
   *  NHIA answers a rejected call with a body explaining itself — a wrong key,
   *  an IP that is not allow-listed, a lapsed subscription. Those need three
   *  different people to put right. This used to throw
   *
   *      `API call failed: ${status} ${statusText}`
   *
   *  and drop the body on the floor, so every one of them reached the screen as
   *  the same four characters and nobody could tell which had happened.        */
  /*  A 401 means NHIA received the credentials and REFUSED them — not that
   *  they were mangled on the way. Padding was the obvious suspect and was
   *  measured and ruled out: HTTP strips leading and trailing whitespace from
   *  a header value in transit, so a padded secret arrives intact and is
   *  accepted. The advice below therefore does not send anyone looking for it. */
  const explain401 = (detail) => [
    'NHIA received the credentials and refused them (401).',
    detail ? `It said: ${detail}` : null,
    'The key and secret stored in Admin Panel are not ones NHIA accepts for this '
      + 'facility. Check them against what NHIA issued — and if they are correct, '
      + 'ask NHIA whether this facility\'s access has been changed, expired, or '
      + 'moved to a different key.',
  ].filter(Boolean).join(' ');

  //  NHIA's body may be JSON or plain text; take whichever says something.
  const bodyText = (data) => {
    if (data == null) return '';
    if (typeof data === 'string') return data.trim().slice(0, 300);
    const named = data.Message || data.message || data.error || data.Error;
    if (named) return String(named).slice(0, 300);
    try { return JSON.stringify(data).slice(0, 300); } catch { return ''; }
  };

  const callNHIAApi = async (requestData) => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      /*  ── THE SERVER ASKS FIRST ──────────────────────────────────────────
       *
       *  Calling elig.nhia.gov.gh straight from this page is why
       *  /api-config/nhia has to hand the API SECRET to the browser at all —
       *  it is readable in the network tab by anyone who can open this dialog.
       *  Going through the server keeps it server-side.
       *
       *  The direct call is KEPT as a fallback rather than deleted, because on
       *  some installs the facility's PC can reach NHIA and the VPS cannot.
       *  `reachable: false` is that case, and only that case — a 401 comes back
       *  reachable, so a refusal is never retried from the browser to be
       *  refused again.                                                      */
      let viaServer = null;
      try {
        const r = await axios.post(nhiaGenCCC, requestData);
        viaServer = r.data;
      } catch (e) {
        viaServer = e?.response?.data || null;
      }

      if (viaServer && viaServer.reachable !== false) {
        if (viaServer.ok) {
          setResponse(viaServer.data);
          return;
        }
        const detail = bodyText(viaServer.data) || viaServer.message || '';
        if (viaServer.status === 401) {
          //  `padded` is reported but NOT offered as the cause — it cannot be
          //  one, see explain401. Worth tidying, unrelated to this failure.
          setError(explain401(detail));
        } else {
          setError(`NHIA returned ${viaServer.status || 'an error'}.`
            + (detail ? ` ${detail}` : ''));
        }
        return;
      }

      //  The server could not see NHIA. Try from here, which is the path that
      //  has always been used, and say why we are doing it if that fails too.
      if (!apiConfig) {
        setError(viaServer?.message
          || 'The server cannot reach NHIA and the API configuration has not loaded.');
        return;
      }

      const url = `${apiConfig.base_url}${apiConfig.endpoint}`;
      const apiResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-nhia-apikey': apiConfig.api_key,
          'x-nhia-apisecret': apiConfig.api_secret,
        },
        body: JSON.stringify(requestData),
      });

      if (!apiResponse.ok) {
        //  Read the body before giving up on it — this is the half that says
        //  which of the three causes it is.
        let detail = '';
        try { detail = bodyText(await apiResponse.clone().json()); }
        catch { try { detail = (await apiResponse.text()).trim().slice(0, 300); } catch { /* none */ } }

        setError(apiResponse.status === 401
          ? explain401(detail)
          : `NHIA returned ${apiResponse.status} ${apiResponse.statusText}.`
            + (detail ? ` ${detail}` : ''));
        console.error('NHIA error', apiResponse.status, detail);
        return;
      }

      setResponse(await apiResponse.json());
    } catch (err) {
      setError(err.message);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOK=()=>{

      onSubmit(response)
      handleClose()
  }

  const handleSubmit = () => {
    // Validate inputs
    if (!cardNo.trim()) {
      setError('Card Number is required');
      return;
    }

    // Prepare request data with user inputs
    const requestData = {
      CardType: cardType,
      CardNo: cardNo.trim()
    };

    callNHIAApi(requestData);
  };

  const handleClose = () => {
    // Reset form state when closing
    setResponse(null);
    setError(null);
    setCardNo(memberNumber);
    setCardType('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      //onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Health Provider Member Validation & CCC Generation
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Membership verification
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* API Configuration Display */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
             Health Provider Member Validation Guide
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>1.</strong> Select <strong>GHANA CARD</strong> for Ghana Card verification or <strong>NHIS CARD</strong> for NHIS membership validation</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>2.</strong> Enter the corresponding  <strong>card number</strong> for membership lookup</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>3.</strong> The System validates membership status and generates CCC</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>4.</strong>  Successful validation returns member details and eligibility information</Typography>
            </Grid>

          </Grid>
        
        </Paper>

        {/* Input Form */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Member Validation Details
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter card details to validate membership and generate CCC
          </Typography>
          
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* Card Type Selection */}
            <Grid item xs={12} sm={6} size={6}>
              <FormControl fullWidth>
                <InputLabel id="card-type-label">Card Type *</InputLabel>
                <Select
                  labelId="card-type-label"
                  value={cardType}
                  label="Card Type *"
                  onChange={(e) => setCardType(e.target.value)}
                >
                  <MenuItem value="GHANACARD">GHANA CARD</MenuItem>
                  <MenuItem value="NHISCARD">NHIS CARD</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Card Number Input */} 
            <Grid item xs={12} sm={6} size={6}>
              <TextField
                fullWidth
                label="Card Number"
                required
                value={cardNo}
                onChange={(e) => setCardNo(e.target.value)}
                placeholder={cardType === 'GHANACARD' ? 'Enter Ghana Card Number' : 'Enter NHIS Card Number'}
                variant="outlined"
              />
            </Grid>
          </Grid>

            <Divider sx={{ my: 1 }} />

          <Grid item xs={12} sm={6} size={12}>

            <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={loading || !cardNo}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ color: 'white' }}
            >
            {loading ? 'Validating...' : 'Validate Member & Generate CCC'}
            </Button>

          </Grid>

        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Processing request...
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>Error Please Check Card Type Or Card Number :</strong> {error}
          </Alert>
        )}

        {/* Success Response */}
        {response && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Member Validation Response:
            </Typography>

            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'background.paper', 
                maxHeight: 300, 
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >

            <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={12} sm={12} size={6}>
              <Typography variant="body2"> Member Name: <strong> {response.MemberName} </strong> </Typography>
            </Grid>
            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Status: <strong>{response.Status} </strong>  </Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">CCC:<strong>{response.MobCCC}</strong></Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6} >
              <Typography variant="body2">Gender: <strong>{response.Gender}</strong></Typography>
            </Grid>
            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Date Of Birth: <strong>{response.DateOfBirth}</strong></Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">EligibilityStartDate: <strong>{response.EligibilityStartDate}</strong></Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Eligibility End Date: <strong>{response.EligibilityEndDate}</strong></Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Attendance Date: <strong>{response.AttendanceDate}</strong></Typography>
            </Grid>

             <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Transaction ID: <strong>{response.TransactionID}</strong></Typography>
            </Grid>

            <Grid item xs={12} sm={6} size={6}>
              <Typography variant="body2">Health Provider: <strong>{response.HPName}</strong></Typography>
            </Grid>

             <Divider sx={{ my: 1 }} />
            
            <Button
            variant="outlined"
            onClick={handleOK}
            fullWidth
            startIcon={ <DoneIcon />}
           
            >
             Ok Done
            </Button>


          </Grid>
            </Paper>

          </Alert>
        )}

       
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>

      {response && (
          <Button
              variant="outlined"
              onClick={handleOK}
          
              startIcon={ <DoneIcon />}
             
              >
              Ok Done
              </Button>
      )}
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
       
      </DialogActions>
    </Dialog>
  );
};


export default Ccc